import React, { ChangeEvent, Fragment, FunctionComponent, useState } from "react";
import { connect, MapStateToProps } from "react-redux";

import { Caption, Display, Layout, MessageWindow, SpacingSize, Text } from "@multiweb/reactComponents";
import { getPrimaryText } from "@multiweb/reactCore/i18n";
import { IAppState, NoneOwnProps } from "@multiweb/types/react";
import { getDefaultActionState } from "../../../../creatorMethodsUtils"; // kvůli prevenci vůči cyklické závislosti je nutné importovat z creatorMethods a ne přes utils
import { ActionType } from "../../../../enums";
import { LOC_ACTION_TYPE, LOC_ATTRIBUTE_CHANGE, LOC_CANCEL, LOC_CHANGE_OF_BINDING_ATTRIBUTE, LOC_DELETE, LOC_DELETE_SELECTED_ACTION, LOC_EMAIL, LOC_EXECUTING_METHOD, LOC_FORM_OPENING, LOC_INCREMENTING_VALUE, LOC_NO_ACTION_CREATED, LOC_SENDING_OF_WF_MESSAGE, LOC_SETTING_VALUE, LOC_SMS, LOC_START_OF_SCRIPT } from "../../../../locKeys";
import { IAction, WorkflowEditorProps } from "../../../../types";
import { SideListFrame, SidePanelItemsSource, WfOption, WfSelect } from "../../components";
import { actionPageRenderer } from "./utils";

type ActionsTabComponentProps = {
    readonly actions: readonly IAction[];
    readonly selectedRecordIndex: number;
    readonly updateActions: (newActions: readonly IAction[]) => void;
    readonly noActionCreatedText: string;
    readonly deleteSelectedActionText: string;
} & WorkflowEditorProps;

export const ActionsTabComponent: FunctionComponent<ActionsTabComponentProps> = ({ hostKey, actions, selectedRecordIndex, updateActions, noActionCreatedText, deleteSelectedActionText }) => {
    const [selectedActionIndex, setSelectedActionIndex] = useState(selectedRecordIndex);
    const [isMessageWindowShown, setIsMessageWindowShown] = useState(false);
    const selectedAction = actions[selectedActionIndex];

    const actionsItemsSource: readonly SidePanelItemsSource[] = actions.map((action, index) => {
        return { headerLocKey: action.locKey, description1: action.name, description2: action.properties["condition"], description3: action.properties["comment"], onItemClick: () => setSelectedActionIndex(index) };
    });

    const onCloseMessageWindow = () => {
        setIsMessageWindowShown(false);
    };

    const changeActionType = (e: ChangeEvent<HTMLSelectElement>) => {
        const newActions = [...actions];
        const actionType: ActionType = e.target.value as ActionType;
        newActions[selectedActionIndex] = getDefaultActionState(actionType);
        updateActions(newActions);
    };

    const addAction = () => {
        const newActions = [...actions, getDefaultActionState(ActionType.CallMethod)];
        updateActions(newActions);
        setSelectedActionIndex(newActions.length - 1);
    };

    const removeAction = () => {
        const newActions = actions.filter((_action, actionIndex) => actionIndex !== selectedActionIndex);
        updateActions(newActions);
        setSelectedActionIndex(selectedActionIndex - 1 >= 0 ? selectedActionIndex - 1 : 0);
    };

    const copyAction = () => {
        const newActions = [...actions];
        newActions.splice(selectedActionIndex + 1, 0, { ...selectedAction });
        updateActions(newActions);
        setSelectedActionIndex(selectedActionIndex + 1);
    };

    const updateSelectedAction = (valueKeyPairs: readonly { key: string; value: string }[]) => {
        const newActions = [...actions];
        const newProperties = { ...selectedAction.properties };

        for (const { key, value } of valueKeyPairs)
            newProperties[key] = value;

        newActions[selectedActionIndex] = { ...selectedAction, properties: newProperties };
        updateActions(newActions);
    };

    const changeSelectedActionOrder = (newIndex: number) => {
        const newActions = [...actions];
        newActions[selectedActionIndex] = newActions[newIndex];
        newActions[newIndex] = selectedAction;
        updateActions(newActions);
        setSelectedActionIndex(newIndex);
    };

    const handleDeleteButtonClick = () => {
        setIsMessageWindowShown(true);
    };

    return (
        <Fragment>
            <SideListFrame
                hostKey={hostKey}
                selectedItemIndex={selectedActionIndex}
                itemsSource={actionsItemsSource}
                onAddItemClick={addAction}
                onRemoveItemClick={handleDeleteButtonClick}
                onCopyItemClick={copyAction}
                onOrderChange={changeSelectedActionOrder}>
                {selectedAction ?
                    <Fragment>
                        <Layout spacingSizeBottom={SpacingSize.m} display={Display.block}>
                            <Caption locKey={LOC_ACTION_TYPE} />
                            <WfSelect qa={"wf-select-action-type"} value={selectedAction.actionType} onChange={changeActionType} {...{ hostKey }}>
                                <WfOption qa={ActionType.CallMethod} value={ActionType.CallMethod} locKey={LOC_EXECUTING_METHOD} />
                                <WfOption qa={ActionType.CallScript} value={ActionType.CallScript} locKey={LOC_START_OF_SCRIPT} />
                                <WfOption qa={ActionType.IncrementValue} value={ActionType.IncrementValue} locKey={LOC_INCREMENTING_VALUE} />
                                <WfOption qa={ActionType.SendMail} value={ActionType.SendMail} locKey={LOC_EMAIL} />
                                <WfOption qa={ActionType.SendSMS} value={ActionType.SendSMS} locKey={LOC_SMS} />
                                <WfOption qa={ActionType.SendWFMessage} value={ActionType.SendWFMessage} locKey={LOC_SENDING_OF_WF_MESSAGE} />
                                <WfOption qa={ActionType.SetAttribute} value={ActionType.SetAttribute} locKey={LOC_ATTRIBUTE_CHANGE} />
                                <WfOption qa={ActionType.SetObjectAttribute} value={ActionType.SetObjectAttribute} locKey={LOC_CHANGE_OF_BINDING_ATTRIBUTE} />
                                <WfOption qa={ActionType.SetValue} value={ActionType.SetValue} locKey={LOC_SETTING_VALUE} />
                                <WfOption qa={ActionType.OpenForm} value={ActionType.OpenForm} locKey={LOC_FORM_OPENING} />
                            </WfSelect>
                        </Layout>
                        {actionPageRenderer(hostKey, selectedAction, updateSelectedAction)}
                    </Fragment> :
                    <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Text value={noActionCreatedText} />
                    </div>}
            </SideListFrame>
            {isMessageWindowShown &&
                <MessageWindow
                    primaryButtonLocKey={LOC_DELETE}
                    secondaryButtonLocKey={LOC_CANCEL}
                    onPrimaryButtonClick={removeAction}
                    {...{ onCloseMessageWindow }}
                >
                    {deleteSelectedActionText}
                </MessageWindow>}
        </Fragment>
    );
};

type StateProps = Pick<ActionsTabComponentProps, "noActionCreatedText" | "deleteSelectedActionText">;

const mapStateToProps: MapStateToProps<StateProps, NoneOwnProps, IAppState> = (state) => ({
    noActionCreatedText: getPrimaryText(state, LOC_NO_ACTION_CREATED) ?? "Žádná akce zatím nebyla vytvořena...",
    deleteSelectedActionText: getPrimaryText(state, LOC_DELETE_SELECTED_ACTION) ?? "Opravdu chcete smazat vybranou akci?",
});

export const ActionsTab = connect(mapStateToProps)(ActionsTabComponent);
